/**
 * iCalendar writer for the household's read-only export feed (D27): planned
 * meals and reminders. All times are written in UTC (`…Z`) so no VTIMEZONE
 * block is needed; calendar apps show them in the viewer's zone.
 *
 * Output follows RFC 5545: CRLF line endings, TEXT escaping, lines folded at
 * 75 octets without splitting a UTF-8 sequence, one DTSTAMP per event, and
 * UIDs supplied by the caller (stable across refreshes so clients update
 * events in place instead of duplicating them).
 */
/** `20261011T180000Z` */
export function formatIcsUtc(d) {
    return d
        .toISOString()
        .replace(/\.\d{3}Z$/, 'Z')
        .replace(/[-:]/g, '');
}
/** TEXT escaping: backslash, semicolon, comma and newlines. */
export function escapeText(value) {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n|\r/g, '\\n');
}
/** UTF-8 length of one code point. */
function utf8Length(ch) {
    const cp = ch.codePointAt(0);
    return cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
}
/** Fold one content line into ≤ 75-octet physical lines joined by CRLF + space. */
export function foldLine(line) {
    const out = [];
    let current = '';
    let size = 0;
    for (const ch of line) {
        const bytes = utf8Length(ch);
        if (size + bytes > 75) {
            // Continuation lines start with a space, which counts toward their 75.
            out.push(current);
            current = ' ';
            size = 1;
        }
        current += ch;
        size += bytes;
    }
    out.push(current);
    return out.join('\r\n');
}
function prop(name, value) {
    return foldLine(`${name}:${value}`);
}
export function buildIcs(input) {
    const stamp = formatIcsUtc(input.now);
    const refresh = `PT${input.refreshHours ?? 6}H`;
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        prop('PRODID', input.prodId ?? '-//FewerBags//Meal plan//EN'),
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        prop('X-WR-CALNAME', escapeText(input.name)),
        `REFRESH-INTERVAL;VALUE=DURATION:${refresh}`,
        `X-PUBLISHED-TTL:${refresh}`,
    ];
    for (const ev of input.events) {
        lines.push('BEGIN:VEVENT', prop('UID', ev.uid), `DTSTAMP:${stamp}`, `DTSTART:${formatIcsUtc(ev.start)}`, `DTEND:${formatIcsUtc(ev.end)}`, prop('SUMMARY', escapeText(ev.summary)));
        if (ev.description)
            lines.push(prop('DESCRIPTION', escapeText(ev.description)));
        if (ev.url)
            lines.push(prop('URL', ev.url));
        if (ev.lastModified)
            lines.push(`LAST-MODIFIED:${formatIcsUtc(ev.lastModified)}`);
        if (ev.alarm) {
            const mins = Math.max(0, Math.round(ev.alarm.minutesBefore));
            lines.push('BEGIN:VALARM', 'ACTION:DISPLAY', prop('DESCRIPTION', escapeText(ev.alarm.description)), `TRIGGER:${mins === 0 ? 'PT0M' : `-PT${mins}M`}`, 'END:VALARM');
        }
        lines.push('END:VEVENT');
    }
    lines.push('END:VCALENDAR');
    return lines.join('\r\n') + '\r\n';
}
//# sourceMappingURL=ics-write.js.map